import React, {useCallback, useEffect, useRef, useState} from "react"
import {
    Typography,
    Pagination,
    Box,
    CircularProgress,
    Card,
    CardContent,
    Grid,
    Chip,
    Avatar,
    Button, CardHeader,
    IconButton,
} from "@mui/material"
import {useTranslation} from "react-i18next"
import i18n from "../../i18n/i18n"
import axiosInstance from "../../config/axios-config"
import {PageContainer, PaginationContainer} from "../../components/common/page-container"
import {ContentContainer} from "../../components/common/content-container"
import {SearchFilterContainer, SearchField} from "../../components/common/search-filter-container"
import LocationOnIcon from "@mui/icons-material/LocationOn"
import PhoneIcon from "@mui/icons-material/Phone"
import StorefrontIcon from "@mui/icons-material/Storefront"
import EmailIcon from "@mui/icons-material/Email"
import VerifiedIcon from "@mui/icons-material/Verified"
import {GetLogo} from "../../profile/main/api/photo-data-handler"
import {Refresh as RefreshIcon} from "@mui/icons-material";
import {useNavigate} from "react-router-dom";
import GlowingMessage from "../../errors/glowing-message";
import ChatPopup from "../../popups/chat-popup";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline"

const PharmaciesTable = ({navOpen}) => {
    const [pharmacies, setPharmacies] = useState([])
    const [page, setPage] = useState(0)
    const [searchQuery, setSearchQuery] = useState("")
    const [refreshVersion, setRefreshVersion] = useState(0)
    const [totalPages, setTotalPages] = useState(0)
    const [loading, setLoading] = useState(true)
    const {t} = useTranslation()
    const [logoUrls, setLogoUrls] = useState({})

    const navigate = useNavigate()
    const [message, setMessage] = useState("")
    const listRequest = useRef(null)

    const [openChatPopup, setOpenChatPopup] = useState(false)
    const [selectedConversation, setSelectedConversation] = useState(null)
    const [loadingConversation, setLoadingConversation] = useState(false)

    const fetchAllLogos = useCallback(async (tenants, signal) => {
        const entries = await Promise.all(tenants.map(async tenant => {
            const logo = tenant.imageUrl ? await GetLogo({filename: tenant.imageUrl}) : null
            return [tenant.id, logo]
        }))
        if (!signal.aborted) setLogoUrls(Object.fromEntries(entries))
    }, [])

    const fetchPharmacies = useCallback(async (page, search) => {
        listRequest.current?.abort()
        const controller = new AbortController()
        listRequest.current = controller
        setLoading(true)
        try {
            const response = await axiosInstance.get("/api/v1/tenants/pharmacies", {
                signal: controller.signal,
                params: {searchTerm: search?.length >= 3 ? search : null, page, size: 9},
                headers: {"Language-Context": i18n.language.toUpperCase() || "EN"},
            })
            if (controller.signal.aborted) return
            const {content = [], totalPages} = response.data
            setPharmacies(content)
            setTotalPages(totalPages)
            await fetchAllLogos(content, controller.signal)
        } catch (error) {
            if (controller.signal.aborted) return
            console.error("Error fetching pharmacies:", error)
            setMessage(error.response?.data?.message || "Failed to get pharmacies.")
        } finally {
            if (!controller.signal.aborted) setLoading(false)
        }
    }, [fetchAllLogos])

    useEffect(() => {
        window.refreshDistributorsData = () => fetchPharmacies(page, searchQuery)
        return () => { delete window.refreshDistributorsData }
    }, [page, searchQuery, fetchPharmacies])

    useEffect(() => {
        listRequest.current?.abort()
        setLoading(true)
        // StrictMode replay cancels the first scheduled load before it starts.
        const timeout = setTimeout(() => fetchPharmacies(page, searchQuery), listRequest.current ? 500 : 0)
        return () => {
            clearTimeout(timeout)
            listRequest.current?.abort()
        }
    }, [page, searchQuery, refreshVersion, t, fetchPharmacies])

    const handlePageChange = (_, newPage) => {
        setPage(newPage - 1)
    }

    const handleViewPharmacy = (pharmacyId) => {
        // Navigate to pharmacy details page
        navigate(`/pharmacies/pharmacy/${pharmacyId}`)
    }

    const handleRefresh = () => {
        setPage(0)
        setSearchQuery("")
        setRefreshVersion(version => version + 1)
    }


    const addressToString = (address) => {
        if (!address) return "";
        return `${address.street || ""}, ${address.city || ""}, ${address.country || ""}`.trim();
    }

    const phoneToString = (phones) => {
        if (!Array.isArray(phones) || phones.length === 0) {
            return "Phones not available";
        }
        return phones
            .map(p => `${p.phoneNumberCode || ""} ${p.phoneNumber || ""}`.trim())
            .join(", ");
    };

    const handleOpenChat = async (pharmacy) => {
        setLoadingConversation(true)
        try {
            // First, check if conversation exists with this pharmacy
            const checkResponse = await axiosInstance.get(`/api/v1/conversations/participants`, {
                params: {
                    participantId: pharmacy.id,
                },
                headers: {
                    "Language-Context": i18n.language.toUpperCase() || "EN",
                },
            })

            let conversation = checkResponse.data

            // If no conversation exists, create a new one
            if (!conversation) {
                const createResponse = await axiosInstance.post(
                    `/api/v1/conversations`,
                    {
                        participantOneId: pharmacy.id
                    },
                    {
                        headers: {
                            "Language-Context": i18n.language.toUpperCase() || "EN",
                        },
                    },
                )
                conversation = createResponse.data
            }

            // Open the chat popup with the conversation
            setSelectedConversation(conversation)
            setOpenChatPopup(true)
        } catch (error) {
            console.error("Error opening conversation:", error)
            setMessage(error.response?.data?.message || "Failed to open conversation.")
        } finally {
            setLoadingConversation(false)
        }
    }

    const handleCloseChatPopup = () => {
        setOpenChatPopup(false)
        setSelectedConversation(null)
    }

    return (
        <>
            <GlowingMessage message={message} onClose={() => setMessage("")}/>
            <PageContainer navOpen={navOpen}>
                <CardHeader
                    title={t("Pharmacies")}
                    subheader={t("PharmaciesTableDesc")}
                    action={
                        <Button variant="outlined" startIcon={<RefreshIcon/>} onClick={handleRefresh}
                                disabled={loading}>
                            {t("Refresh")}
                        </Button>
                    }
                />
                <ContentContainer elevation={2} sx={{padding: 3}}>
                    <SearchFilterContainer>
                        <SearchField
                            placeholder={t("Search")}
                            variant="outlined"
                            size="small"
                            value={searchQuery}
                            onChange={(e) => {
                                setSearchQuery(e.target.value)
                                setPage(0)
                            }}
                        />
                    </SearchFilterContainer>

                    {loading ? (
                        <Box display="flex" justifyContent="center" alignItems="center" height={400}>
                            <CircularProgress/>
                        </Box>
                    ) : pharmacies.length === 0 ? (
                        <Typography
                            variant="h6"
                            fontWeight="bold"
                            sx={{
                                padding: 4,
                                textAlign: "center",
                            }}
                        >
                            {t("NoPharmaciesFound")}
                        </Typography>
                    ) : (
                        <Grid container spacing={0} sx={{mt: 3, mb: 3}}>
                            {pharmacies.map((pharmacy, index) => {
                                // Calculate if this is first or last in a row
                                // For a 3-column layout on desktop
                                const isFirstInRow = index % 3 === 0
                                const isLastInRow = index % 3 === 2

                                // Format address as a string
                                const addressString = addressToString(pharmacy.address);

                                // Format all phone numbers into a single string
                                const phoneNumbersString = phoneToString(pharmacy.phones);

                                // Get the logo URL from our state
                                const logoUrl = logoUrls[pharmacy.id] || "/placeholder.svg?height=80&width=80"

                                return (
                                    <Grid
                                        item
                                        xs={12}
                                        sm={6}
                                        md={4}
                                        key={pharmacy.id}
                                        sx={{
                                            pl: isFirstInRow ? 0 : 2,
                                            pr: isLastInRow ? 0 : 2,
                                            mb: 3,
                                        }}
                                    >
                                        <Card
                                            sx={{
                                                height: "100%",
                                                display: "flex",
                                                flexDirection: "column",
                                                transition: "transform 0.2s, box-shadow 0.2s",
                                                "&:hover": {
                                                    transform: "translateY(-4px)",
                                                    boxShadow: "0 8px 16px rgba(0,0,0,0.1)",
                                                },
                                            }}
                                        >
                                            <CardContent sx={{flexGrow: 1, display: "flex", flexDirection: "column"}}>
                                                <Box sx={{display: "flex", alignItems: "center", mb: 2}}>
                                                    <Avatar src={logoUrl} alt={pharmacy.tenantNameEn}
                                                            sx={{width: 60, height: 60, mr: 2}}/>
                                                    <Box>
                                                        <Box sx={{display: "flex", alignItems: "center"}}>
                                                            <Typography variant="h6" component="div"
                                                                        sx={{fontWeight: 500}}>
                                                                {pharmacy.tenantNameEn}
                                                            </Typography>
                                                            <VerifiedIcon color="primary" sx={{ml: 1, fontSize: 20}}/>
                                                        </Box>
                                                        <Chip
                                                            icon={<StorefrontIcon/>}
                                                            label={pharmacy.tenantType === "PHARMACY" ? "PHARMACY" : "WHOLESALE"}
                                                            size="small"
                                                            color="primary"
                                                            variant="outlined"
                                                            sx={{mt: 0.5}}
                                                        />
                                                    </Box>
                                                </Box>

                                                <Box sx={{mb: 1.5}}>
                                                    <Box sx={{display: "flex", alignItems: "flex-start", mb: 1}}>
                                                        <LocationOnIcon fontSize="small"
                                                                        sx={{color: "text.secondary", mr: 1, mt: 0.3}}/>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {addressString}
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{display: "flex", alignItems: "flex-start", mb: 1}}>
                                                        <PhoneIcon fontSize="small"
                                                                   sx={{color: "text.secondary", mr: 1, mt: 0.3}}/>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {phoneNumbersString}
                                                        </Typography>
                                                    </Box>

                                                    <Box sx={{display: "flex", alignItems: "flex-start"}}>
                                                        <EmailIcon fontSize="small"
                                                                   sx={{color: "text.secondary", mr: 1, mt: 0.3}}/>
                                                        <Typography variant="body2" color="text.secondary" noWrap>
                                                            {pharmacy.tenantEmail}
                                                        </Typography>
                                                    </Box>
                                                </Box>

                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        justifyContent: "space-between",
                                                        alignItems: "center",
                                                        mt: "auto",
                                                    }}
                                                >
                                                    <Button variant="contained" size="small"
                                                            onClick={() => handleViewPharmacy(pharmacy.id)}>
                                                        {t("View")}
                                                    </Button>
                                                    <IconButton
                                                        color="primary"
                                                        onClick={() => handleOpenChat(pharmacy)}
                                                        disabled={loadingConversation}
                                                        sx={{
                                                            "&:hover": {
                                                                backgroundColor: "primary.light",
                                                                color: "white",
                                                            },
                                                        }}
                                                    >
                                                        <ChatBubbleOutlineIcon/>
                                                    </IconButton>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                )
                            })}
                        </Grid>
                    )}

                    <PaginationContainer>
                        <Pagination
                            count={totalPages}
                            page={page + 1}
                            onChange={handlePageChange}
                            color="primary"
                            variant="outlined"
                            shape="rounded"
                            siblingCount={1}
                        />
                    </PaginationContainer>
                </ContentContainer>
            </PageContainer>
            {openChatPopup && selectedConversation && (
                <ChatPopup open={openChatPopup} onClose={handleCloseChatPopup} conversation={selectedConversation}/>
            )}
        </>
    )
}

export default PharmaciesTable
